import React from "react";
import {
  Box,
  Button,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import type {
  ClientUser,
} from "../api/clientManagement";

interface UserRowProps {
  user: ClientUser;
  statusLabel: string;
  statusTone: "success" | "muted";
  onOpenEdit: () => void;
}

export const UserRow: React.FC<UserRowProps> = ({
  user,
  statusLabel,
  statusTone,
  onOpenEdit,
}) => {
  const consentStatus = user.consent_status
    ? user.consent_status
    : user.requires_consent
      ? "required"
      : "completed";
  const consentLabel =
    consentStatus === "withdrawn"
      ? "Withdrawn"
      : consentStatus === "completed"
        ? "Completed"
        : "Required";
  const consentColor =
    consentStatus === "withdrawn"
      ? "error.dark"
      : consentStatus === "completed"
        ? "success.dark"
        : "warning.dark";
  const consentBg =
    consentStatus === "withdrawn"
      ? "rgba(211, 47, 47, 0.12)"
      : consentStatus === "completed"
        ? "rgba(46, 125, 50, 0.12)"
        : "rgba(237, 108, 2, 0.12)";
  const wearStatus = user.wear_jeopardy_status ?? null;
  const wearStyle = wearStatus ? WEAR_STATUS_COLORS[wearStatus] : null;
  const wearLabel = wearStatus ? WEAR_STATUS_LABELS[wearStatus] ?? wearStatus : "-";

  return (
    <TableRow hover onClick={onOpenEdit} sx={{ cursor: "pointer" }}>
    <TableCell>
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={600}>
          {user.first_name} {user.last_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user.email}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click row to edit
        </Typography>
      </Stack>
    </TableCell>
    <TableCell>
      <Typography variant="body2" color="text.secondary">
        {user.departments.length
          ? user.departments.map((dept) => dept.name).join(", ")
          : "-"}
      </Typography>
    </TableCell>
    <TableCell>
      <Typography variant="body2" color="text.secondary">
        {user.job_roles.length
          ? user.job_roles.map((role) => role.name).join(", ")
          : "-"}
      </Typography>
    </TableCell>
    <TableCell>
      <Stack spacing={1}>
        {SERVICE_LABELS.map((service) => (
          <Typography key={service.key} variant="caption" color="text.secondary">
            {service.label}: {user.enabled_services?.[service.key] ? "Enabled" : "Disabled"}
          </Typography>
        ))}
      </Stack>
    </TableCell>
    <TableCell>
      {wearStatus ? (
        <Stack spacing={0.5}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.5,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              color: wearStyle?.color,
              backgroundColor: wearStyle?.backgroundColor,
            }}
          >
            {wearLabel}
          </Box>
          {user.wear_jeopardy_status_reason && (
            <Typography variant="caption" color="text.secondary">
              {user.wear_jeopardy_status_reason}
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      )}
    </TableCell>
    <TableCell>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1,
          py: 0.5,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          color: consentColor,
          backgroundColor: consentBg,
        }}
      >
        {consentLabel}
      </Box>
    </TableCell>
    <TableCell>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1,
          py: 0.5,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          color: statusTone === "success" ? "success.dark" : "text.secondary",
          backgroundColor:
            statusTone === "success"
              ? "rgba(46, 125, 50, 0.12)"
              : "action.selected",
        }}
      >
        {statusLabel}
      </Box>
    </TableCell>
    <TableCell>
      <Button
        variant="text"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onOpenEdit();
        }}
      >
        Edit
      </Button>
    </TableCell>
    </TableRow>
  );
};

const SERVICE_LABELS = [
  { key: "learning_locker", label: "Learning Locker" },
  { key: "occupational_health", label: "Occupational Health" },
  { key: "wearables", label: "Wearables" },
  { key: "akea_life", label: "Akea Life" },
] as const;

const WEAR_STATUS_LABELS: Record<string, string> = {
  not_worn: "Not worn",
  intermittent_wear: "Intermittent wear",
  not_connected: "Not connected",
  recently_connected: "Recently connected",
  wearing_consistently: "Wearing consistently",
};

const WEAR_STATUS_COLORS: Record<
  string,
  { color: string; backgroundColor: string }
> = {
  not_worn: { color: "#991b1b", backgroundColor: "#fee2e2" },
  intermittent_wear: { color: "#92400e", backgroundColor: "#fef3c7" },
  not_connected: { color: "#374151", backgroundColor: "#e5e7eb" },
  recently_connected: { color: "#1e3a8a", backgroundColor: "#dbeafe" },
  wearing_consistently: { color: "#166534", backgroundColor: "#dcfce7" },
};
